import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { confirmToast } from "../../lib/confirmToast";
import { formatPlan, formatStatus } from "../../lib/display";
import { Ban, Plus, Pencil, Store, Search, Loader2 } from "lucide-react";

const AdminLojas = () => {
  const { user } = useAuth();
  const [lojas, setLojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cnpj: "",
    cidade: "",
    estado: "",
    plano: "basico",
    codigo_empresa_erp: "",
  });
  const canManageLojas = user?.is_master || user?.tipo === "master" || (
    user?.tipo === "admin" && user?.admin_role !== "gerente"
  );

  useEffect(() => {
    fetchLojas();
  }, []);

  const fetchLojas = async () => {
    try {
      const response = await api.get("/lojas");
      setLojas(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar lojas."));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/lojas/${editing}`, formData);
        toast.success("Loja atualizada com sucesso!");
      } else {
        await api.post("/lojas", formData);
        toast.success("Loja criada com sucesso!");
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData({ nome: "", cnpj: "", cidade: "", estado: "", plano: "basico", codigo_empresa_erp: "" });
      fetchLojas();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao salvar loja."));
    }
  };

  const handleEdit = (loja) => {
    setEditing(loja.id);
    setFormData({
      nome: loja.nome,
      cnpj: loja.cnpj,
      cidade: loja.cidade,
      estado: loja.estado,
      plano: loja.plano,
      codigo_empresa_erp: loja.codigo_empresa_erp || "",
    });
    setDialogOpen(true);
  };

  const deactivateLoja = async (id) => {
    try {
      await api.delete(`/lojas/${id}`);
      toast.success("Loja inativada com sucesso!");
      fetchLojas();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao inativar loja."));
    }
  };

  const handleDelete = (id) => {
    confirmToast({
      title: "Inativar loja?",
      description: "Ela deixará de aparecer nas listas operacionais, mantendo o histórico preservado.",
      confirmLabel: "Inativar",
      variant: "danger",
      onConfirm: () => deactivateLoja(id),
    });
  };

  const filteredLojas = lojas.filter(
    (loja) =>
      loja.nome.toLowerCase().includes(search.toLowerCase()) ||
      loja.cidade.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-lojas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Lojas</h1>
          <p className="text-slate-600 mt-1">Gerencie as lojas do programa</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canManageLojas && (
            <DialogTrigger asChild>
              <Button
                className="btn-primary"
                onClick={() => {
                  setEditing(null);
                  setFormData({ nome: "", cnpj: "", cidade: "", estado: "", plano: "basico", codigo_empresa_erp: "" });
                }}
                data-testid="add-loja-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Loja
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editing ? "Editar Loja" : "Nova Loja"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da loja</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="input-field"
                  required
                  data-testid="loja-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  className="input-field"
                  required
                  data-testid="loja-cnpj-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo_empresa_erp">Código da empresa no ERP</Label>
                <Input
                  id="codigo_empresa_erp"
                  value={formData.codigo_empresa_erp}
                  onChange={(e) => setFormData({ ...formData, codigo_empresa_erp: e.target.value })}
                  className="input-field"
                  placeholder="Ex.: 021"
                  data-testid="loja-codigo-erp-input"
                />
                <p className="text-xs text-slate-500">
                  Necessário para identificar vendas quando um parceiro participa de mais de uma loja.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    className="input-field"
                    required
                    data-testid="loja-cidade-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Input
                    id="estado"
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="input-field"
                    maxLength={2}
                    required
                    data-testid="loja-estado-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="plano">Plano</Label>
                <select
                  id="plano"
                  value={formData.plano}
                  onChange={(e) => setFormData({ ...formData, plano: e.target.value })}
                  className="input-field w-full"
                  data-testid="loja-plano-select"
                >
                  <option value="basico">Básico</option>
                  <option value="profissional">Profissional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-loja-btn">
                  {editing ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar lojas..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
          data-testid="search-lojas-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Loja
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Localização
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Plano
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLojas.map((loja) => (
                  <tr key={loja.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Store className="h-5 w-5 text-primary" />
                        </div>
                        <span className="font-medium text-slate-900">{loja.nome}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{loja.cnpj}</td>
                    <td className="px-6 py-4 text-slate-600">
                      {loja.cidade}, {loja.estado}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                        {formatPlan(loja.plano)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        loja.status === "ativo" 
                          ? "bg-green-100 text-green-700" 
                          : "bg-red-100 text-red-700"
                      }`}>
                        {formatStatus(loja.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {canManageLojas && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(loja)}
                            className="text-slate-500 hover:text-primary"
                            data-testid={`edit-loja-${loja.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(loja.id)}
                            className="text-slate-500 hover:text-red-500"
                            title="Inativar loja"
                            aria-label="Inativar loja"
                            data-testid={`delete-loja-${loja.id}`}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredLojas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      {search ? "Nenhuma loja encontrada" : "Nenhuma loja cadastrada ainda"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLojas;
